import { useState } from "react";
import { Link } from "react-router-dom";
import { useBackOrigin, useBackTo } from "@/hooks/useBackTo";
import { Clock, Pencil, Trash2 } from "lucide-react";
import { Loading } from "@/components/Loading";
import { Notice } from "@/components/Notice";
import { SearchInput } from "@/components/SearchInput";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatDate } from "@/utils/dates";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { TemplatePreview } from "@/features/tickets/components/TemplatePreview";
import {
  ticketTemplates as templatesApi,
  type TicketTemplate,
  type TicketTemplateList,
} from "@/features/tickets/api/ticketTemplates.api";

const PAGE_SIZE = 10;

export default function TicketTemplatesPage() {
  const confirm = useConfirm();
  const origin = useBackOrigin();
  const ticketsBackTo = useBackTo("/tickets");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    items,
    meta,
    keyword,
    setKeyword,
    loading,
    loadingMore,
    loaded,
    error,
    forbidden,
    totalCount,
    hasMore,
    sentinelRef,
    removeItem,
  } = useInfiniteList<TicketTemplate, TicketTemplateList>(templatesApi.list, {
    pageSize: PAGE_SIZE,
    syncUrl: true,
  });

  const canCreate = meta?.canCreate === true;
  const searching = keyword.trim().length > 0;

  async function remove(template: TicketTemplate) {
    const confirmed = await confirm({
      title: "Delete template?",
      description: `"${template.name}" will no longer be available when raising a ticket. This can't be undone.`,
      confirmLabel: "Delete template",
      destructive: true,
    });
    if (!confirmed) return;
    setBusyId(template.id);
    setDeleteError(null);
    try {
      await templatesApi.remove(template.id);
      removeItem(template.id);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Failed to delete the template.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to={ticketsBackTo} className="text-sm text-muted-foreground hover:underline">
            ← Back to tickets
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-slate-800">Ticket templates</h1>
          <p className="mt-1 text-sm text-slate-500">
            Ready-made ticket descriptions your team can start from.
          </p>
        </div>
        {canCreate && (
          <Button asChild className="shrink-0 self-start">
            <Link to="/tickets/templates/new" state={origin}>New template</Link>
          </Button>
        )}
      </header>

      {forbidden && (
        <Notice tone="warn">
          You need at least one ticket template permission to view templates. Ask an
          administrator to grant one.
        </Notice>
      )}
      {error && !forbidden && <Notice tone="error">{error}</Notice>}
      {deleteError && <Notice tone="error">{deleteError}</Notice>}

      {!forbidden && (
        <>
          {loaded && (
            <SearchInput
              value={keyword}
              onSearch={setKeyword}
              placeholder="Search templates…"
              className="mb-4 ml-0 w-full"
              adornment={totalCount > 0 ? `${items.length} of ${totalCount}` : null}
            />
          )}

          {loading && !loaded ? (
            <Loading>Loading templates…</Loading>
          ) : items.length === 0 ? (
            searching ? (
              <Notice tone="info">No templates match “{keyword.trim()}”.</Notice>
            ) : (
              !error && (
                <Card>
                  <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                    <p className="text-sm font-medium text-slate-700">No templates yet</p>
                    <p className="max-w-md text-sm text-slate-500">
                      Save the ticket descriptions your team writes most often, then insert them
                      into a ticket, comment or reply in one click.
                    </p>
                    {canCreate && (
                      <Button asChild className="mt-1">
                        <Link to="/tickets/templates/new" state={origin}>New template</Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )
            )
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((t) => (
                <Card key={t.id}>
                  <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
                    <div className="min-w-0">
                      <h2 className="break-words text-base font-semibold text-slate-900">
                        {t.name}
                      </h2>
                      <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        {t.updatedAt === t.createdAt ? "Created" : "Updated"}{" "}
                        {formatDate(t.updatedAt)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {t.canEdit && (
                        <Button asChild variant="outline" size="icon" aria-label={`Edit ${t.name}`}>
                          <Link to={`/tickets/templates/${t.id}/edit`} state={origin}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Link>
                        </Button>
                      )}
                      {t.canDelete && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={`Delete ${t.name}`}
                          disabled={busyId === t.id}
                          onClick={() => remove(t)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <TemplatePreview name={t.name} content={t.content} />
                  </CardContent>
                </Card>
              ))}

              {hasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}
              {loadingMore && (
                <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
                  <Spinner className="h-4 w-4" />
                  Loading more…
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
